import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Play, Pause, SkipForward, SkipBack, Volume2, Loader2 } from 'lucide-react';

const AudioPlayer = ({ src, title, onEnded, onNext, onPrev, showControls = true }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      setPlaying(false);
      setProgress(0);
    }
  }, [src]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    onEnded?.();
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  return (
    <div className="bg-gray-50 border rounded-lg p-3" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleTimeUpdate}
        preload="metadata"
      />
      <div className="flex items-center gap-3">
        {showControls && onPrev && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onPrev}>
            <SkipBack className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-9 w-9 p-0 rounded-full bg-blue-100 hover:bg-blue-200"
          onClick={togglePlay}
          data-testid="audio-play-btn"
        >
          {playing ? <Pause className="h-4 w-4 text-blue-700" /> : <Play className="h-4 w-4 text-blue-700 ml-0.5" />}
        </Button>
        {showControls && onNext && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onNext}>
            <SkipForward className="h-4 w-4" />
          </Button>
        )}
        <div className="flex-1 min-w-0">
          {title && <p className="responsive-title text-xs text-gray-700 font-medium truncate">{title}</p>}
          <div
            className="h-1.5 bg-gray-200 rounded-full cursor-pointer mt-1"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
            />
          </div>
        </div>
        <span className="text-xs text-gray-500 tabular-nums w-12 text-right">
          {formatTime(progress)}/{formatTime(duration)}
        </span>
        <Volume2 className="h-3.5 w-3.5 text-gray-400" />
      </div>
    </div>
  );
};

export default AudioPlayer;
